import { DragHandleIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
} from "@chakra-ui/react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { FieldArray, Form, Formik, getIn, useFormikContext } from "formik";
import { InputControl, InputControlProps } from "formik-chakra-ui";
import React from "react";
import { RiAddLine, RiArrowUpSLine, RiDeleteBin6Line } from "react-icons/ri";
import { NavigationBlocker } from "src/components/NavigationBlocker";
import {
  generateKey,
  isUserGeneratedKey,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { validateWithZod } from "src/services/formValidations";
import {
  createOfferingCategoryFormSchema,
  emptyOption,
  type OfferingCategoryFormValues,
} from "./offeringCategoryForm";

type Props = {
  initialValues: OfferingCategoryFormValues;
  siblingKeys: readonly string[];
  submitLabel: string;
  isDeleting?: boolean;
  onSubmit: (values: OfferingCategoryFormValues) => Promise<void>;
  onCancel: () => void;
  onDelete: () => void;
};

export const OfferingCategoryEditForm: React.FC<Props> = ({
  initialValues,
  siblingKeys,
  submitLabel,
  isDeleting = false,
  onSubmit,
  onCancel,
  onDelete,
}) => {
  const droppableId = React.useId();

  return (
    <Formik<OfferingCategoryFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validateWithZod(createOfferingCategoryFormSchema(siblingKeys))}
    >
      {(formik) => {
        const isBusy = formik.isSubmitting || isDeleting;

        return (
          <Flex as={Form} noValidate direction="column">
            <NavigationBlocker hasChanges={formik.dirty} />
            <Flex
              gap={2}
              p={4}
              align="flex-end"
              bg="white"
              borderBottomWidth="1px"
              borderColor="gray.200"
              borderTopRadius="xl"
            >
              <Flex
                boxSize="32px"
                align="center"
                justify="center"
                color="gray.400"
                flexShrink={0}
                aria-hidden
              >
                <RiArrowUpSLine />
              </Flex>
              <NameAndKeyFields
                labelCaption="Offering category name"
                keyCaption="Offering category key"
              />
              <IconButton
                type="button"
                aria-label="Delete offering category"
                icon={<RiDeleteBin6Line />}
                variant="outline"
                colorScheme="gray"
                onClick={onDelete}
                isDisabled={formik.isSubmitting}
                isLoading={isDeleting}
              />
            </Flex>

            <FieldArray name="options">
              {(helpers) => {
                const handleDragEnd = (result: DropResult) => {
                  if (result.destination === null) return;
                  if (result.source.index === result.destination.index) return;
                  helpers.move(result.source.index, result.destination.index);
                };
                const optionsError =
                  typeof formik.errors.options === "string"
                    ? formik.errors.options
                    : undefined;

                return (
                  <>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable
                        droppableId={droppableId}
                        isDropDisabled={isBusy}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {formik.values.options.map((option, index) => (
                              <Draggable
                                key={option._reactId}
                                draggableId={option._reactId}
                                index={index}
                                isDragDisabled={isBusy}
                              >
                                {(draggableProvided, snapshot) => (
                                  <Flex
                                    ref={draggableProvided.innerRef}
                                    {...draggableProvided.draggableProps}
                                    gap={2}
                                    p={4}
                                    align="flex-end"
                                    bg={
                                      snapshot.isDragging
                                        ? "white"
                                        : "blackAlpha.50"
                                    }
                                    boxShadow={
                                      snapshot.isDragging ? "md" : undefined
                                    }
                                    borderBottomWidth="1px"
                                    borderColor="gray.200"
                                  >
                                    <Box
                                      {...draggableProvided.dragHandleProps}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      boxSize="32px"
                                      cursor={isBusy ? "not-allowed" : "grab"}
                                      color="gray.500"
                                      aria-label="Drag to reorder offering option"
                                    >
                                      <DragHandleIcon w={3} h={3} />
                                    </Box>
                                    <NameAndKeyFields
                                      namePrefix={`options.${index}`}
                                      labelCaption="Offering option name"
                                      keyCaption="Offering option key"
                                    />
                                    <IconButton
                                      type="button"
                                      aria-label="Delete offering option"
                                      icon={<RiDeleteBin6Line />}
                                      variant="outline"
                                      colorScheme="gray"
                                      isDisabled={isBusy}
                                      onClick={() => helpers.remove(index)}
                                    />
                                  </Flex>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    </DragDropContext>

                    <FormControl
                      isInvalid={optionsError != null}
                      bg="blackAlpha.50"
                      pl={14}
                      pr={4}
                      py={4}
                    >
                      <Button
                        type="button"
                        variant="outline"
                        colorScheme="gray"
                        size="sm"
                        leftIcon={<RiAddLine />}
                        isDisabled={isBusy}
                        onClick={() => helpers.push(emptyOption())}
                      >
                        Add offering option
                      </Button>
                      <FormErrorMessage>{optionsError}</FormErrorMessage>
                    </FormControl>
                  </>
                );
              }}
            </FieldArray>

            <Flex
              justify="flex-end"
              gap={2}
              p={4}
              bg="white"
              borderTopWidth="1px"
              borderColor="gray.200"
              borderBottomRadius="xl"
            >
              <Button
                type="button"
                variant="ghost"
                colorScheme="primary"
                size="sm"
                onClick={onCancel}
                isDisabled={isBusy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                isLoading={formik.isSubmitting}
                isDisabled={isBusy || !formik.isValid}
              >
                {submitLabel}
              </Button>
            </Flex>
          </Flex>
        );
      }}
    </Formik>
  );
};

const nameAndKeyStyleProps = {
  flex: 1,
  minW: 0,
  marginBottom: 0,
  labelProps: {
    fontSize: "sm",
    fontWeight: "medium",
    color: "gray.700",
    mb: 1.5,
  },
} as const satisfies Partial<InputControlProps>;

function NameAndKeyFields({
  namePrefix,
  labelCaption,
  keyCaption,
}: {
  namePrefix?: string;
  labelCaption: string;
  keyCaption: string;
}) {
  const formik = useFormikContext<OfferingCategoryFormValues>();
  const labelName = namePrefix ? `${namePrefix}.label` : "label";
  const keyName = namePrefix ? `${namePrefix}.key` : "key";
  const persistedId: string | undefined = namePrefix
    ? getIn(formik.values, `${namePrefix}.id`)
    : formik.values.id;

  return (
    <Flex flex="1" gap={6} minW={0} align="flex-start">
      <InputControl
        name={labelName}
        label={labelCaption}
        {...nameAndKeyStyleProps}
        inputProps={{
          onChange: (event) => {
            const nextLabel = event.target.value;
            const previousLabel = getIn(formik.values, labelName);
            const previousKey = getIn(formik.values, keyName);

            void formik.setFieldValue(labelName, nextLabel);
            if (
              !persistedId &&
              !isUserGeneratedKey(previousKey, previousLabel)
            ) {
              void formik.setFieldValue(keyName, generateKey(nextLabel));
            }
          },
        }}
      />
      <InputControl
        name={keyName}
        label={keyCaption}
        {...nameAndKeyStyleProps}
      />
    </Flex>
  );
}
